import FilterAltIcon from '@mui/icons-material/FilterAlt';
import MinimizeIcon from '@mui/icons-material/Minimize';
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fab,
  Grid,
  IconButton,
  Stack,
  Zoom,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useContractList } from '../../../services/contractList';
import { useFilters } from '../../../services/filters';
import {
  ContractListRequest,
  Locations,
  SoldToParty,
} from '../../../services/types/contractListRequest';
import { BaseController } from '../../BaseController/baseController';
import { useFilterContext } from '../../context/filterContext';
import { ErrorBox } from '../../error/error';
import { GoToTopButton } from '../../goToTopButton/goToTopButton';
import { ContractCards } from '../contractCards/contractCards';
import { Filters } from '../filters/filters';

const ContractList = () => {
  const { t } = useTranslation();

  const [firstLaunch, setFirstLaunch] = useState(true);
  const [resetPressed, setResetPressed] = useState(false);
  const [menu, setMenu] = useState(true);
  const [filterButton, setFilterButton] = useState(false);
  const [contractListRequestTmp, setContractListRequestTmp] =
    useState<ContractListRequest | null>();

  const {
    filters,
    isLoading: filtersIsLoading,
    serverError: filterServerError,
  } = useFilters();

  const currentFilterContext = useFilterContext();

  const { contractList, isLoading, serverError } = useContractList(
    contractListRequestTmp,
  );

  useEffect(() => {
    if (!filtersIsLoading) {
      const tmpLocations: Locations = { soldToParties: [] };
      const tmpShipToParties: string[] = [];

      // iterate through locationRights
      filters?.locationRights.forEach((locationRight) => {
        if (
          !currentFilterContext.divisions.includes(
            locationRight.divisionNr.toString(),
          )
        ) {
          // discard de-selected divisions
          return;
        }

        // iterate through countries in locationRight
        locationRight.countries.forEach((locationRightCountry) => {
          if (
            !currentFilterContext.countries
              .flatMap((c) => JSON.parse(c.value))
              .flat()
              .includes(locationRightCountry.countryNr.toString())
          ) {
            // discard de-selected countries (within a selected division)
            return;
          }

          locationRightCountry.locations.forEach((location) => {
            location.shipToParty.forEach((l) => {
              if (!tmpShipToParties.find((tmps) => tmps === l.id.toString())) {
                tmpShipToParties.push(l.id.toString());
              }
            });
            tmpLocations.soldToParties.push({
              id: location.soldToParty.toString(),
              shipToParties: location.shipToParty.map((stp) => {
                return { id: stp.id.toString() };
              }),
            });
          });
        });
      });

      // Clean up SoldToParties and ShipToParties according to ShipToParty filters
      const cleanLocations: Locations = { soldToParties: [] };
      tmpLocations.soldToParties.forEach((sotp) => {
        const newSoldToParty: SoldToParty = {
          id: sotp.id,
          shipToParties: sotp.shipToParties.filter((shtp) => shtp.id !== '0'),
        };
        if (newSoldToParty.shipToParties.length > 0) {
          cleanLocations.soldToParties.push(newSoldToParty);
        }
      });

      currentFilterContext.setLocations(cleanLocations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFilterContext.shipToParties,
    currentFilterContext.countries,
    currentFilterContext.divisions,
  ]);

  const handleOnFilterPress = (
    filterUpToRows?: number,
    filterOffset?: number,
  ) => {
    let upToRowsTmp;

    if (filterUpToRows || filterUpToRows === 0) {
      upToRowsTmp = filterUpToRows;
    } else {
      upToRowsTmp = currentFilterContext.upToRows;
    }

    let offSetTmp;
    if (filterOffset || filterOffset === 0) {
      offSetTmp = filterOffset;
      currentFilterContext.offset = filterOffset;
    } else {
      offSetTmp = currentFilterContext.offset;
    }

    let tmpLocations: Locations = { soldToParties: [] };

    const shipToPartiesCount =
      currentFilterContext.locations.soldToParties.reduce(
        (res, { shipToParties }) => res + shipToParties.length,
        0,
      );
    if (currentFilterContext.shipToParties.length !== shipToPartiesCount) {
      const filteredLocations: Locations = { soldToParties: [] };

      currentFilterContext.locations.soldToParties.forEach((soldToParty) => {
        const newSoldToParty: SoldToParty = {
          id: soldToParty.id,
          shipToParties: soldToParty.shipToParties.filter((shtp) =>
            currentFilterContext.shipToParties.includes(shtp.id),
          ),
        };
        if (newSoldToParty.shipToParties.length > 0) {
          filteredLocations.soldToParties.push(newSoldToParty);
        }
      });

      if (filteredLocations.soldToParties.length > 0) {
        tmpLocations = filteredLocations;
      }
    } else {
      tmpLocations = currentFilterContext.locations;
    }

    setContractListRequestTmp({
      locations: tmpLocations,
      beginDateStart: currentFilterContext.beginDateStart,
      beginDateEnd: currentFilterContext.beginDateEnd,
      upToRows: upToRowsTmp,
      offset: offSetTmp,
      contractTypes:
        currentFilterContext.contractType === ''
          ? []
          : [currentFilterContext.contractType.toString()],
      contractUsages:
        currentFilterContext.contractUsage === ''
          ? []
          : [currentFilterContext.contractUsage.toString()],
      contractNumber: currentFilterContext.contractNumber,
      contractStatus: currentFilterContext.contractStatus,
      orderNumber: currentFilterContext.customerReference,
      equipmentNumber: currentFilterContext.serialNumber,
    });
  };

  useEffect(() => {
    if (
      resetPressed &&
      currentFilterContext.locations.soldToParties.length > 0
    ) {
      handleOnFilterPress();
    }
    setResetPressed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPressed]);

  useEffect(() => {
    if (
      firstLaunch &&
      currentFilterContext.locations.soldToParties.length > 0
    ) {
      handleOnFilterPress();
      setFirstLaunch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterContext.locations]);

  const handleChangePage = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    currentFilterContext.setOffset(newPage);
    handleOnFilterPress(currentFilterContext.upToRows, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    currentFilterContext.setUpToRows(parseInt(event.target.value, 10));
    currentFilterContext.setOffset(0);

    handleOnFilterPress(parseInt(event.target.value, 10), 0);
  };

  if (filtersIsLoading) {
    return (
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!isLoading && serverError) {
    return (
      <ErrorBox
        errorHeader={t('MissingRights')}
        errorText={t([
          `${serverError}`,
          'GeneralError',
          'general server error',
        ])}
      />
    );
  }

  if (!filtersIsLoading && filterServerError) {
    if (filterServerError === 'NoLocations') {
      return (
        <ErrorBox
          errorHeader={t('MissingRights')}
          errorText={t('NoLocations')}
        />
      );
    }

    if (filterServerError === 'NoAppPermission') {
      return (
        <ErrorBox
          errorHeader={t('MissingRights')}
          errorText={t('NoAppPermission')}
        />
      );
    }

    return (
      <ErrorBox
        errorHeader={t('MissingRights')}
        errorText={t([
          `${filterServerError}`,
          'GeneralError',
          `filter server error`,
        ])}
      />
    );
  }

  const handleChangeFilterButton = () => {
    setMenu((prev: any) => !prev);
    setFilterButton((prev: any) => !prev);
  };

  return (
    <>
      <Zoom
        in={filterButton}
        mountOnEnter
        unmountOnExit
        style={{ transitionDelay: menu ? '0ms' : '0ms' }}
      >
        <Fab
          variant="extended"
          sx={{
            margin: 0,
            top: 'auto',
            left: 30,
            bottom: 30,
            right: 'auto',
            position: 'fixed',
            borderRadius: 0,
          }}
          color="primary"
          aria-label="filter"
          onClick={handleChangeFilterButton}
        >
          <FilterAltIcon />
          Filter
        </Fab>
      </Zoom>

      <GoToTopButton />

      <Box
        sx={{
          width: '100%',
          minHeight: '80vh',
          backgroundColor: 'rgb(241,241,241)',
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            paddingBottom: '1em',
          }}
          spacing={1}
        >
          <Grid container sx={{ maxWidth: '1200px', alignItems: 'center' }}>
            <Stack direction="row" justifyContent="flex-start" width="100%">
              <Stack width="100%" direction="row" spacing={1}>
                <Zoom
                  in={menu}
                  mountOnEnter
                  unmountOnExit
                  style={{ transitionDelay: menu ? '0ms' : '0ms' }}
                >
                  <Stack>
                    <Card
                      sx={{
                        maxWidth: '20em',
                        width: {
                          sm: '10em',
                          md: '10em',
                          lg: '30vw',
                          xl: '30vw',
                        },
                        borderRadius: 0,
                      }}
                    >
                      <Stack alignItems="flex-end" spacing={0}>
                        <IconButton
                          color="secondary"
                          aria-label="upload picture"
                          component="label"
                          onClick={handleChangeFilterButton}
                        >
                          <MinimizeIcon />
                        </IconButton>
                      </Stack>
                      <CardContent>
                        <Filters
                          filters={filters}
                          handleOnFilterPress={handleOnFilterPress}
                          setResetPressed={setResetPressed}
                        />
                      </CardContent>
                    </Card>
                  </Stack>
                </Zoom>
                <Stack sx={{ width: '100%' }} spacing={1}>
                  <ContractCards
                    isLoading={isLoading}
                    contractList={contractList}
                    mappingFunc={
                      BaseController.mapResponseToInternalContractList
                    }
                  />

                  {!isLoading && contractList.contracts.length > 0 ? (
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      style={{ alignSelf: 'center', justifySelf: 'center' }}
                      component="div"
                      count={contractList.rowCount}
                      labelRowsPerPage={t('RowsPerPage')}
                      // labelDisplayedRows={({ count, page }) => {
                      //   const pages: number = Math.floor(
                      //     count / currentFilterContext.upToRows,
                      //   );
                      //   return `${page + 1} of ${pages + 1}`;
                      // }}
                      page={currentFilterContext.offset}
                      onPageChange={handleChangePage}
                      rowsPerPage={currentFilterContext.upToRows}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      showFirstButton
                      showLastButton
                    />
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        </Stack>
      </Box>
    </>
  );
};

export default ContractList;
